// Accessibility Utilities for EAA 2025 Compliance
// European Accessibility Act requirements (effective June 28, 2025)

// Skip to main content functionality
export const addSkipToMainContent = () => {
  // Check if skip link already exists
  if (document.getElementById('skip-to-main')) return;

  const skipLink = document.createElement('a');
  skipLink.id = 'skip-to-main';
  skipLink.href = '#main-content';
  skipLink.textContent = 'Zum Hauptinhalt springen';
  skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded focus:shadow-lg';
  
  // Insert as first element in body
  document.body.insertBefore(skipLink, document.body.firstChild);
};

// Announce dynamic content changes to screen readers
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

// Focus management for modals and dynamic content
export const trapFocus = (element) => {
  const focusableElements = element.querySelectorAll(
    'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];
  
  const handleTabKey = (e) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    }
    
    if (e.key === 'Escape') {
      // Close modal or return focus
      const closeButton = element.querySelector('[aria-label*="schließen"], [aria-label*="close"]');
      if (closeButton) {
        closeButton.click();
      }
    }
  };
  
  element.addEventListener('keydown', handleTabKey);
  
  // Focus first element
  if (firstFocusableElement) {
    firstFocusableElement.focus();
  }
  
  return () => {
    element.removeEventListener('keydown', handleTabKey);
  };
};

// Check color contrast ratio (WCAG 2.1 Level AA requires 4.5:1 for normal text)
export const checkColorContrast = (foreground, background) => {
  const getLuminance = (color) => {
    const rgb = color.match(/\d+/g);
    const [r, g, b] = rgb.map(c => {
      c = parseInt(c) / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  
  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  
  return {
    ratio: ratio.toFixed(2),
    passAA: ratio >= 4.5,
    passAAA: ratio >= 7
  };
};

// Add keyboard navigation support
export const addKeyboardNavigation = () => {
  // Add visible focus indicators
  const style = document.createElement('style');
  style.textContent = `
    .focus-visible:focus {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
      border-radius: 4px;
    }
    
    .focus-visible:focus:not(:focus-visible) {
      outline: none;
    }
  `;
  document.head.appendChild(style);
  
  // Add focus-visible class to interactive elements
  const interactiveElements = document.querySelectorAll(
    'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
  );
  
  interactiveElements.forEach(element => {
    element.classList.add('focus-visible');
  });
};

// Ensure proper heading hierarchy
export const validateHeadingHierarchy = () => {
  const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
  const issues = [];
  let previousLevel = 0;
  
  headings.forEach((heading, index) => {
    const currentLevel = parseInt(heading.tagName.charAt(1));
    
    if (index === 0 && currentLevel !== 1) {
      issues.push(`First heading should be h1, found ${heading.tagName.toLowerCase()}`);
    }
    
    if (currentLevel > previousLevel + 1) {
      issues.push(`Heading level skipped: ${heading.tagName.toLowerCase()} follows h${previousLevel}`);
    }
    
    previousLevel = currentLevel;
  });
  
  if (issues.length > 0) {
    console.warn('Heading hierarchy issues found:', issues);
  }
  
  return issues;
};

// Add alt text validation for images
export const validateImageAltText = () => {
  const images = document.querySelectorAll('img');
  const issues = [];
  
  images.forEach((img, index) => {
    if (!img.hasAttribute('alt')) {
      issues.push(`Image ${index + 1} missing alt attribute`);
    } else if (img.alt === '') {
      // Empty alt is okay for decorative images, but should be intentional
      if (!img.hasAttribute('role') || img.getAttribute('role') !== 'presentation') {
        console.info(`Image ${index + 1} has empty alt text - ensure this is decorative`);
      }
    }
  });
  
  if (issues.length > 0) {
    console.warn('Image accessibility issues found:', issues);
  }
  
  return issues;
};

// Add ARIA landmarks
export const addARIALandmarks = () => {
  // Add main landmark if not present
  const main = document.querySelector('main');
  if (main && !main.hasAttribute('role')) {
    main.setAttribute('role', 'main');
    main.setAttribute('id', 'main-content');
  }
  
  // Add navigation landmarks
  const navs = document.querySelectorAll('nav');
  navs.forEach((nav, index) => {
    if (!nav.hasAttribute('aria-label') && !nav.hasAttribute('aria-labelledby')) {
      nav.setAttribute('aria-label', `Navigation ${index + 1}`);
    }
  });
  
  // Add banner and contentinfo roles
  const header = document.querySelector('header');
  if (header && !header.hasAttribute('role')) {
    header.setAttribute('role', 'banner');
  }
  
  const footer = document.querySelector('footer');
  if (footer && !footer.hasAttribute('role')) {
    footer.setAttribute('role', 'contentinfo');
  }
};

// Initialize all accessibility features
export const initializeAccessibility = () => {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupAccessibilityFeatures();
    });
  } else {
    setupAccessibilityFeatures();
  }
};

const setupAccessibilityFeatures = () => {
  addSkipToMainContent();
  addKeyboardNavigation();
  addARIALandmarks();
  
  // Validate accessibility (development only)
  if (process.env.NODE_ENV === 'development') {
    setTimeout(() => {
      validateHeadingHierarchy();
      validateImageAltText();
    }, 1000);
  }
  
  // Add reduced motion support
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (prefersReducedMotion.matches) {
    document.documentElement.classList.add('reduce-motion');
  }
  
  prefersReducedMotion.addEventListener('change', (e) => {
    if (e.matches) {
      document.documentElement.classList.add('reduce-motion');
    } else {
      document.documentElement.classList.remove('reduce-motion');
    }
  });
};
